import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Users, FileText, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatDateTimeBR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function Approvals() {
  const [contracts, setContracts] = useState(null);
  const [docs, setDocs] = useState(null);

  const refresh = () => {
    api.get("/contracts").then(({ data }) => setContracts(data));
    api.get("/documents", { params: { status_filter: "pending_director" } }).then(({ data }) => setDocs(data));
  };

  useEffect(refresh, []);

  if (!contracts || !docs) return <Skeleton className="h-96" />;

  const pendingAssignments = contracts.filter((c) => c.consultant_pending_director);

  const approveConsultant = async (id) => {
    try {
      await api.post(`/contracts/${id}/approve-consultant`);
      toast.success("Designação aprovada.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-midnight" /> Aprovações pendentes
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Designações de consultor e documentos aguardando sua aprovação como Diretora.
        </p>
      </div>

      <Tabs defaultValue="consultants">
        <TabsList>
          <TabsTrigger value="consultants" data-testid="tab-consultants">
            <Users className="w-4 h-4 mr-2" /> Designações ({pendingAssignments.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-2" /> Documentos ({docs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="consultants">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base">Designação de consultor</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingAssignments.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">
                  Nenhuma designação pendente.
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingAssignments.map((c) => (
                    <div
                      key={c.id}
                      className="p-4 border border-slate-200 rounded-md flex items-center justify-between"
                      data-testid={`pending-consultant-${c.id}`}
                    >
                      <div>
                        <div className="font-medium text-slate-900">
                          {c.client_name} — {c.product_name}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          Consultor sugerido pela Secretaria:{" "}
                          <strong>{c.pending_consultant_name || "—"}</strong>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => approveConsultant(c.id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`approve-consultant-${c.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aprovar
                        </Button>
                        <Link to={`/diretora/contratos/${c.id}`}>
                          <Button size="sm" variant="outline">
                            Detalhes <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base">Documentos pendentes de aprovação da Diretora</CardTitle>
            </CardHeader>
            <CardContent>
              {docs.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">
                  Nenhum documento pendente.
                </div>
              ) : (
                <div className="space-y-3">
                  {docs.map((d) => (
                    <div
                      key={d.id}
                      className="p-4 border border-slate-200 rounded-md flex items-center justify-between"
                      data-testid={`pending-doc-${d.id}`}
                    >
                      <div>
                        <div className="font-medium text-slate-900">{d.title}</div>
                        <div className="text-xs text-slate-600 mt-0.5 inline-flex items-center gap-2">
                          <StatusBadge status={d.status} />
                          <span>Criado em {formatDateTimeBR(d.created_at)}</span>
                        </div>
                      </div>
                      <Link to={`/documentos/${d.id}`}>
                        <Button size="sm" variant="outline" data-testid={`review-doc-${d.id}`}>
                          Revisar <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
